import { z } from 'zod'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import { zSetting } from 'app/frontend/base/helpers/forms'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import DestructiveField from 'app/frontend/base/components/form-fields/DestructiveField'
import { useTranslation } from 'react-i18next'
import { useUpdateTeam } from 'app/frontend/base/config/mutations'
import DroptimizerConfigurationFormContent, {
  EditableDroptimizerConfiguration,
} from '../DroptimizerConfigurationFormContent'
import { useTeamDroptimizerConfigurations } from 'app/frontend/base/config/queries'
import { useQueryClient } from '@tanstack/react-query'

const formSchema = z.object({
  settings: z.object({
    wishlistAutoClear: zSetting(z.coerce.number().min(0).max(720)),
    wishlistDifficulties: zSetting(z.array(z.string())),
    wishlistEditable: zSetting(z.boolean()),
    wishlistLocked: zSetting(z.boolean()),
    wishlistMinimumThreshold: zSetting(z.coerce.number().min(0)),
    wishlistOverviewVisibility: zSetting(
      z.enum(['entire_team', 'admins_only']),
    ),
    wishlistLootHistoryVisibility: zSetting(
      z.enum(['entire_team', 'admins_only']),
    ),
  }),
  deleteLootHistory: z.boolean().optional(),
  deleteWishlists: z.boolean().optional(),
  droptimizer_configurations_attributes: z.array(
    z.object({
      id: z.number().optional(),
      name: z.string(),
      fightStyle: z.string(),
      sockets: z.boolean(),
      pi: z.boolean(),
      matchEquippedGear: z.boolean(),
      numberOfBossesOperator: z.enum([
        'smaller',
        'smaller_or_equal',
        'equal',
        'greater_or_equal',
        'greater',
      ]),
      numberOfBosses: z.number(),
      fightDurationOperator: z.enum([
        'smaller',
        'smaller_or_equal',
        'equal',
        'greater_or_equal',
        'greater',
      ]),
      fightDuration: z.number(),
      weight: z.number(),
      upgradeLevel: z.number(),
      upgradeLevelHeroic: z.number(),
      upgradeLevelNormal: z.number(),
      upgradeLevelLfr: z.number(),
      expertMode: z.boolean(),
      _destroy: z.boolean().optional(),
    }),
  ),
})

const LootSection = () => {
  const { team } = useTeam()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutate, isPending } = useUpdateTeam<
    wowaudit.Team & { deleteLootHistory?: boolean; deleteWishlists?: boolean }
  >(team)
  const {
    data: droptimizerConfigurations,
    isPending: loadingDroptimizerConfigurations,
  } = useTeamDroptimizerConfigurations(team.id)

  if (loadingDroptimizerConfigurations) return null

  return (
    <>
      <FormWrapper<
        wowaudit.Team & {
          deleteLootHistory?: boolean
          deleteWishlists?: boolean
          droptimizer_configurations_attributes: EditableDroptimizerConfiguration[]
        }
      >
        key={team?.id}
        formSchema={formSchema}
        entity={{
          ...team,
          droptimizer_configurations_attributes:
            droptimizerConfigurations ?? [],
        }}
        onSave={async (form, data) => {
          const newConfigurations = await queryClient.fetchQuery<
            wowaudit.DroptimizerConfiguration[]
          >({
            queryKey: ['teamDroptimizerConfigurations', { id: team?.id }],
          })

          form.reset({
            ...data,
            droptimizer_configurations_attributes: newConfigurations ?? [],
          })
        }}
        entityType='team'
      >
        <SettingField name='wishlistDifficulties' />
        <SettingField name='wishlistAutoClear' layout='box' />
        <SettingField name='wishlistMinimumThreshold' layout='box' />

        <h5>Allowed Droptimizer configurations</h5>
        <p className='mb-4'>
          You can set up multiple Droptimizer configurations here. If you have
          more than one, the weight of each will be used for the upgrade values
          shown in the overview.
        </p>
        <p className='mb-6'>
          For example, you might have one configuration for single target, and
          one configuration for AoE (both with a weight of 1). Members can
          upload their reports for each configuration separately, so if an item
          is a 1% upgrade on single target and a 3% upgrade on AoE then the
          overview will show 2%. If the user only fills in the single target
          configuration with a 1% upgrade, it will show 0.5%.
        </p>

        <DroptimizerConfigurationFormContent />

        <h5 className='mt-4'>Member rights</h5>

        <SettingField name='wishlistEditable' layout='box' />
        <SettingField name='wishlistLocked' layout='box' />
      </FormWrapper>

      <h5 className='mt-12'>Danger zone</h5>

      <DestructiveField
        className='mt-4'
        label={t('forms.fields.team.clearLootHistory.label')}
        buttonText={t('shared.reset')}
        description={t('forms.fields.team.clearLootHistory.description')}
        confirmation={t('forms.fields.team.clearLootHistory.confirmation')}
        onDelete={() => mutate({ deleteLootHistory: true })}
        loading={isPending}
      />

      <DestructiveField
        className='mt-4'
        label={t('forms.fields.team.deleteWishlists.label')}
        buttonText={t('shared.reset')}
        description={t('forms.fields.team.deleteWishlists.description')}
        confirmation={t('forms.fields.team.deleteWishlists.confirmation')}
        onDelete={() => mutate({ deleteWishlists: true })}
        loading={isPending}
      />
    </>
  )
}

export default LootSection
